import { useState, useEffect, useRef } from 'react';
import { submitAttempt } from '../services/api';
import { useAuth } from '../context/AuthContext';
import './LogicForge.css';

const MAX_TRIES = 3;

export default function LogicForge({ puzzle, onSolve, alreadyAttempted, onPlayAgain, loadingPlayAgain }) {
  const { user, fetchCredits } = useAuth();

  const [assignments, setAssignments] = useState({});
  const [submitted, setSubmitted]     = useState(false);
  const [correct, setCorrect]         = useState(false);
  const [wrong, setWrong]             = useState({});
  const [tries, setTries]             = useState(0);
  const [gameOver, setGameOver]       = useState(false);
  const [showSolution, setShowSolution] = useState(false);
  const [shareText, setShareText]     = useState('');
  const [hintLevel, setHintLevel]   = useState(0);
  const [showHints, setShowHints]   = useState(false);
  const [elapsed, setElapsed]         = useState(0);
  const startTime                     = useRef(Date.now());
  const timerRef                      = useRef(null);

  // Timer — persists across tab switches using sessionStorage
  useEffect(() => {
    if (!puzzle?.id) return;
    const key = `lf_start_${puzzle.id}`;
    // Only set start time if not already set for this puzzle
    if (!sessionStorage.getItem(key)) {
      sessionStorage.setItem(key, Date.now().toString());
    }
    startTime.current = parseInt(sessionStorage.getItem(key));

    timerRef.current = setInterval(() => {
      if (!gameOver) {
        setElapsed(Math.floor((Date.now() - startTime.current) / 1000));
      }
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [puzzle?.id]);

  // Stop timer when game over
  useEffect(() => {
    if (gameOver) clearInterval(timerRef.current);
  }, [gameOver]);

  // Reset state when puzzle changes
  useEffect(() => {
    setAssignments({});
    setSubmitted(false);
    setCorrect(false);
    setWrong({});
    setTries(0);
    setGameOver(false);
    setShowSolution(false);
    setElapsed(0);
  }, [puzzle?.id]);

  if (!puzzle?.content) return <div className="lf-loading mono">Loading puzzle...</div>;
  if (loadingPlayAgain) return (
    <div className="lf-loading mono" style={{textAlign:'center', padding:'40px 20px'}}>
      <div style={{fontSize:'32px', marginBottom:'12px'}}>⏳</div>
      <div>✨ Generating new puzzle powered by AI</div>
    </div>
  );
  if (alreadyAttempted) return (
    <div className="lf-loading" style={{textAlign:'center', padding:'40px 20px'}}>
      <div style={{fontSize:'32px', marginBottom:'12px', filter:'none', opacity:1}}><span style={{color:'initial'}}>✅</span></div>
      <div style={{fontSize:'16px', fontWeight:'700', color:'#f0f4ff', marginBottom:'8px'}}>Already completed today!</div>
      <div style={{fontSize:'12px', color:'rgba(240,244,255,0.5)', marginBottom:'16px'}}>Use 1 credit to play a new puzzle.</div>
      {onPlayAgain
        ? <button onClick={onPlayAgain} style={{fontSize:'12px', color:'#4fe3c1', background:'rgba(79,227,193,0.1)', border:'1px solid rgba(79,227,193,0.35)', borderRadius:'8px', padding:'8px 16px', cursor:'pointer'}}>✨ Play again — 1 credit</button>
        : <a href="/upgrade" style={{fontSize:'12px', color:'#e8c547', textDecoration:'none'}}>Get more credits →</a>
      }
    </div>
  );

  const { setup, people, jobs, cities, clues, solution, emoji, title } = puzzle.content;
  const categories = { Job: jobs, City: cities };
  const triesLeft = MAX_TRIES - tries;

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    return m > 0 ? `${m}m ${s % 60}s` : `${s}s`;
  };

  const handleSelect = (person, category, value) => {
    if (submitted || gameOver) return;
    setAssignments(prev => ({ ...prev, [`${person}-${category}`]: value }));
  };

  const isComplete = () => people.every(p => assignments[`${p}-Job`] && assignments[`${p}-City`]);

  const handleSubmit = () => {
    if (!isComplete() || gameOver) return;

    const wrongCells = {};
    let allCorrect = true;
    solution.forEach(({ person, job, city }) => {
      if (assignments[`${person}-Job`] !== job) { wrongCells[`${person}-Job`] = true; allCorrect = false; }
      if (assignments[`${person}-City`] !== city) { wrongCells[`${person}-City`] = true; allCorrect = false; }
    });

    const newTries = tries + 1;
    setWrong(wrongCells);
    setSubmitted(true);
    setCorrect(allCorrect);
    setTries(newTries);

    const isLastTry = newTries >= MAX_TRIES;
    if (allCorrect || isLastTry) {
      setGameOver(true);
      const timeTaken = Math.floor((Date.now() - startTime.current) / 1000);
      if (user && puzzle) {
        submitAttempt({
          puzzle_id: puzzle.id,
          solved: allCorrect,
          num_guesses: newTries,
          guesses: [],
          score: allCorrect ? Math.max(10, 100 - (newTries - 1) * 25) : 0,
          time_taken_seconds: timeTaken,
        }).then(() => { if (fetchCredits) fetchCredits(); }).catch(console.error);
      }
      if (allCorrect && onSolve) onSolve(allCorrect);
      const th = puzzle?.content?.theme_emoji && puzzle?.content?.title ? `${puzzle.content.emoji} ${puzzle.content.title}\n` : '';
      const timeTaken2 = Math.floor((Date.now() - startTime.current) / 1000);
      const mins2 = Math.floor(timeTaken2/60); const secs2 = timeTaken2%60;
      const timeStr2 = mins2 > 0 ? `${mins2}m ${secs2}s` : `${secs2}s`;
      const dots2 = Array.from({length:MAX_TRIES}).map((_,i) => i < newTries ? (allCorrect && i===newTries-1?'🟩':'🟥') : '⬜').join('');
      setShareText(`${th}🧩 LogicForge\n${allCorrect ? `Solved in ${newTries} tr${newTries===1?'y':'ies'} · ${timeStr2} ✅` : `Not solved · ${timeStr2} ❌`}\n${dots2}\n\n🎮 Play today → puzzleforge.io`);
    }
  };

  const handleRetry = () => {
    setAssignments({});
    setSubmitted(false);
    setCorrect(false);
    setWrong({});
    // timer intentionally NOT reset
  };

  return (
    <div className="lf-wrap">
      <div className="lf-header">
        <div className="lf-label">
          <span className="lf-emoji">{emoji || '🧩'}</span>
          <div>
            <span className="lf-tag mono">{title || "Today's Challenge"}</span>
            <span className="lf-title mono">LogicForge</span>
          </div>
        </div>
        <div className="lf-header-right">
          <span className="lf-timer mono">⏱ {formatTime(elapsed)}</span>
          {!gameOver && tries > 0 && (
            <span className="lf-tries-left mono">{triesLeft} tr{triesLeft === 1 ? 'y' : 'ies'} left</span>
          )}
        </div>
      </div>

      <p className="lf-setup">{setup}</p>

      {/* Tries dots */}
      <div className="lf-tries-dots">
        {Array.from({length: MAX_TRIES}).map((_, i) => (
          <div key={i} className={`lf-try-dot ${i < tries ? (correct && i === tries-1 ? 'correct' : 'wrong') : ''}`} />
        ))}
      </div>

      {/* Clues */}
      <div className="lf-clues">
        <h3 className="mono lf-clues-title">Clues:</h3>
        <ul className="lf-clue-list">
          {clues.map((clue, i) => (
            <li key={i} className="lf-clue mono">
              <span className="lf-clue-num">{i + 1}</span>
              {clue}
            </li>
          ))}
        </ul>
      </div>

      {/* Grid */}
      <div className="lf-grid-wrap">
        <table className="lf-grid">
          <thead>
            <tr>
              <th className="mono">Person</th>
              {Object.keys(categories).map(cat => (
                <th key={cat} className="mono">{cat}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {people.map(person => (
              <tr key={person}>
                <td className="lf-person mono">{person}</td>
                {Object.entries(categories).map(([cat, options]) => {
                  const key = `${person}-${cat}`;
                  const val = assignments[key];
                  const isWrong = wrong[key];
                  const isRight = submitted && !isWrong && val;
                  return (
                    <td key={cat} className={`lf-cell ${isWrong ? 'wrong' : ''} ${isRight ? 'correct' : ''}`}>
                      <select
                        className="lf-select mono"
                        value={val || ''}
                        onChange={e => handleSelect(person, cat, e.target.value)}
                        disabled={gameOver}
                      >
                        <option value="">— pick —</option>
                        {options.map(opt => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Result */}
      {submitted && (
        <div className={`lf-result ${correct ? 'correct' : 'wrong'}`}>
          <div className="lf-result-icon">{correct ? '🎉' : gameOver ? '😔' : '✗'}</div>
          <div className="lf-result-text mono">
            {correct
              ? `Solved in ${tries} tr${tries === 1 ? 'y' : 'ies'} · ${formatTime(elapsed)}`
              : gameOver
              ? `Out of tries! Time: ${formatTime(elapsed)}`
              : `Not quite — ${triesLeft} tr${triesLeft === 1 ? 'y' : 'ies'} left`}
          </div>
          {shareText && gameOver && (
            <button className="lf-share mono" onClick={() => navigator.clipboard.writeText(shareText).catch(()=>{})}>
              📋 Copy Result
            </button>
          )}
          {gameOver && !correct && (
            <button className="lf-reveal mono" onClick={() => setShowSolution(!showSolution)}>
              {showSolution ? 'Hide' : 'Show'} solution
            </button>
          )}
        </div>
      )}

      {showSolution && (
        <div className="lf-solution">
          <h3 className="mono">Solution:</h3>
          {solution.map(({ person, job, city }) => (
            <div key={person} className="lf-sol-row mono">
              <span className="lf-sol-person">{person}</span>
              <span className="lf-sol-arrow">→</span>
              <span>{job}</span>
              <span className="lf-sol-sep">·</span>
              <span>{city}</span>
            </div>
          ))}
        </div>
      )}

      {!gameOver && submitted && !correct && (
        <button className="lf-retry mono" onClick={handleRetry}>
          Try again ({triesLeft} left) — timer keeps running
        </button>
      )}

      {/* Hints */}
      {!gameOver && (
        <div className="lf-hints-wrap">
          <button
            className="lf-hint-btn mono"
            onClick={() => { if (puzzle?.content?.hints?.length > 0) { setShowHints(true); setHintLevel(h => Math.min(h + 1, puzzle.content.hints.length)); }}}
            disabled={!puzzle?.content?.hints?.length || hintLevel >= (puzzle?.content?.hints?.length || 0)}
          >
            💡 {!puzzle?.content?.hints?.length ? 'Hints unavailable' : hintLevel === 0 ? 'Get a hint' : hintLevel >= puzzle.content.hints.length ? 'No more hints' : 'Next hint'}
          </button>
          {showHints && hintLevel > 0 && (
            <div className="lf-hints-list">
              {puzzle.content.hints.slice(0, hintLevel).map((hint, i) => (
                <div key={i} className="lf-hint mono">
                  <span className="lf-hint-num">{i + 1}</span> {hint}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {!submitted && !gameOver && (
        <button className="lf-submit mono" onClick={handleSubmit} disabled={!isComplete()}>
          {isComplete() ? 'Check Solution →' : `Fill ${people.length * 2 - Object.keys(assignments).length} more cells`}
        </button>
      )}
    </div>
  );
}
